import { z } from 'zod';
import { EVENT_CONFIG, type EventKey } from './event-config';
import type { NotificationPreferencesData } from './types';

/**
 * Utility to get keys as a tuple for z.enum()
 */
function getKeys<T extends Record<string, unknown>>(obj: T): [keyof T, ...(keyof T)[]] {
  return Object.keys(obj) as [keyof T, ...(keyof T)[]];
}

/**
 * Type for EVENT_PREFERENCES
 * Maps each event key to its Zod schema with catch fallback
 */
type EventPreferencesType = {
  [K in EventKey]: z.ZodCatch<z.ZodTypeAny>;
};

/**
 * Generates EVENT_PREFERENCES object from EVENT_CONFIG
 * Each event gets its schema with a .catch() fallback to the default value
 */
function generateEventPreferences(): EventPreferencesType {
  const preferences = {} as EventPreferencesType;

  for (const key of Object.keys(EVENT_CONFIG) as EventKey[]) {
    const config = EVENT_CONFIG[key];
    const schema = config.schema as z.ZodSchema<unknown>;
    preferences[key] = schema.catch(config.default);
  }

  return preferences;
}

/**
 * Generated EVENT_PREFERENCES
 * Single source of truth for all notification event preference schemas
 */
export const EVENT_PREFERENCES = generateEventPreferences();

/**
 * EventKey schema for validation
 */
export const EventKeySchema = z.enum(getKeys(EVENT_CONFIG));

/**
 * Type for DEFAULT_PREFERENCES
 * Maps each event key to its default value
 */
type DefaultPreferencesType = {
  [K in EventKey]: (typeof EVENT_CONFIG)[K]['default'];
};

/**
 * Generates DEFAULT_PREFERENCES object from EVENT_CONFIG
 * Extracts the default value for each event
 */
function generateDefaultPreferences(): DefaultPreferencesType {
  const defaults = {} as DefaultPreferencesType;

  for (const key of Object.keys(EVENT_CONFIG) as EventKey[]) {
    const config = EVENT_CONFIG[key];
    (defaults as Record<string, typeof config.default>)[key] = config.default;
  }

  return defaults;
}

/**
 * Generated DEFAULT_PREFERENCES
 * Default values for all notification preferences
 */
export const DEFAULT_PREFERENCES = generateDefaultPreferences();

/**
 * Transforms flat Preferences data into categorized NotificationPreferencesData
 *
 * This function is derived from EVENT_CONFIG, so adding a new event
 * automatically updates this mapping.
 */
export function updateDataFormatHelper(data: Record<string, unknown>): NotificationPreferencesData {
  const result: NotificationPreferencesData = {
    document: {} as NotificationPreferencesData['document'],
    signature: {} as NotificationPreferencesData['signature'],
    expiry: {} as NotificationPreferencesData['expiry'],
  };

  for (const key of Object.keys(EVENT_CONFIG) as EventKey[]) {
    const config = EVENT_CONFIG[key];
    const category = config.category;

    result[category][key] = data[key];
  }

  return result;
}
